import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useMerchantSettingsContext } from "~/context/MerchantSettingsContext/MerchantSettingsContext";
import { useDHLDownStatus } from "~/hooks/useDHLDownStatus/useDHLDownStatus";
import { useCookiesStatus } from "~/hooks/useCookiesSettings/useCookiesStatus";
import { useDetectTranslationMismatch } from "~/hooks/useDetectTranslationMismatch";
import { pathResolvers } from "~/router/routes/path";
import { useOrderDetails } from "~/hooks/useOrderDetails/useOrderDetails";
import { isNotNil } from "~/util/isNotNil";
import { useLoginStateContext } from "~/context/LoginStateContext/LoginStateContext";
import { mixpanelTrackUser } from "~/util/mixpanel";
import { ErrorToast } from "../common/Toast/Toast";

import {
  getLoginFormDataFromRootDocument,
  removeLoginFormDataFromRootDocument,
} from "./index.util";

export const useLogin = () => {
  const { push } = useHistory();
  const { t } = useTranslation();
  const [fetchOrderDetailsEnabled, setFetchOrderDetailsEnabled] =
    useState(false);

  const fetchOrder = () => setFetchOrderDetailsEnabled(true);
  const disableFetchOrder = () => setFetchOrderDetailsEnabled(false);

  const { orderId, customerEmail, setOrderId, setCustomerEmail, isDisableCTA } =
    useLoginStateContext();

  const {
    data: {
      merchant: { name, climateCompensation },
      returnPolicy: { policyUrl },
    },
  } = useMerchantSettingsContext();

  const { isLoading, isError, errorMessage, data } = useOrderDetails(
    {
      orderId,
      customerEmail,
      currentPage: 1,
    },
    () => {
      mixpanelTrackUser({
        merchantName: name,
        orderId,
        customerEmail,
      });
      push(pathResolvers.SelectItems);
    },
    fetchOrderDetailsEnabled
  );

  useEffect(() => {
    if (isNotNil(data?.order?.id)) {
      push(pathResolvers.SelectItems);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      disableFetchOrder();
    }
  }, [isError]);

  const { isCookiesDisabled } = useCookiesStatus();

  const { isDHLDown } = useDHLDownStatus();

  useEffect(() => {
    if (isDHLDown) {
      const errorMessage = t("backend:dhl_down_message");
      ErrorToast({
        message: errorMessage,
        toastId: "dhlDownError",
        closeButton: false,
      });
    }
  }, [isDHLDown]);

  useEffect(() => {
    if (isCookiesDisabled) {
      const errorMessage = t("backend:cookie_disabled_message");
      ErrorToast({
        message: errorMessage,
        toastId: "cookiesDisabled",
        closeButton: false,
      });
    }
  }, [isCookiesDisabled]);

  useEffect(() => {
    const { orderId, email } = getLoginFormDataFromRootDocument();
    if (orderId && email) {
      setCustomerEmail(email);
      setOrderId(orderId);
      removeLoginFormDataFromRootDocument();
      fetchOrder();
    }
  }, []);

  useDetectTranslationMismatch();

  return {
    orderId,
    customerEmail,
    setOrderId,
    setCustomerEmail,
    isDisableCTA: isDisableCTA || isLoading,
    isLoading,
    isError,
    errorMessage,
    name,
    climateCompensation,
    policyUrl,
    isDHLDown,
    fetchOrder,
  };
};
